<template>
  <section
    class="benefit-form"
    role="form"
    aria-live="polite"
  >
    <header class="benefit-form__header">
      <h3 class="benefit-form__title">{{ t("benefit_form.title") }}</h3>
      <p class="benefit-form__subtitle">
        {{ t("benefit_form.subtitle") }}
      </p>
    </header>

    <form
      class="benefit-form__body"
      novalidate
      @submit.prevent="handleSubmit"
    >
      <div
        v-if="success"
        class="benefit-form__state benefit-form__state--success"
        role="status"
      >
        <h4>{{ t("benefit_form.success_title") }}</h4>
        <p>{{ t("benefit_form.success_body") }}</p>
      </div>

      <div
        v-else
        class="benefit-form__grid"
      >
        <label class="benefit-form__field">
          <span>{{ t("benefit_form.doc_number") }}</span>
          <input
            id="benefit-doc-number"
            v-model.trim="form.doc_number"
            type="text"
            inputmode="numeric"
            autocomplete="off"
            :placeholder="t('benefit_form.doc_placeholder')"
            :aria-invalid="Boolean(fieldErrors.doc_number)"
            :aria-describedby="fieldErrors.doc_number ? 'benefit-error-doc' : undefined"
            required
          />
          <small
            v-if="fieldErrors.doc_number"
            id="benefit-error-doc"
            class="benefit-form__error"
          >
            {{ fieldErrors.doc_number }}
          </small>
        </label>

        <label class="benefit-form__field">
          <span>{{ t("benefit_form.full_name") }}</span>
          <input
            id="benefit-full-name"
            v-model.trim="form.full_name"
            type="text"
            autocomplete="name"
            :placeholder="t('benefit_form.full_name_placeholder')"
            :aria-invalid="Boolean(fieldErrors.full_name)"
            :aria-describedby="fieldErrors.full_name ? 'benefit-error-name' : undefined"
            required
          />
          <small
            v-if="fieldErrors.full_name"
            id="benefit-error-name"
            class="benefit-form__error"
          >
            {{ fieldErrors.full_name }}
          </small>
        </label>

        <label class="benefit-form__field">
          <span>{{ t("benefit_form.phone") }}</span>
          <input
            id="benefit-phone"
            v-model.trim="form.phone"
            type="tel"
            autocomplete="tel"
            :placeholder="t('benefit_form.phone_placeholder')"
            :aria-invalid="Boolean(fieldErrors.phone)"
            :aria-describedby="fieldErrors.phone ? 'benefit-error-phone' : undefined"
            required
          />
          <small
            v-if="fieldErrors.phone"
            id="benefit-error-phone"
            class="benefit-form__error"
          >
            {{ fieldErrors.phone }}
          </small>
        </label>

        <label class="benefit-form__field">
          <span>{{ t("benefit_form.whatsapp") }}</span>
          <input
            id="benefit-whatsapp"
            v-model.trim="form.whatsapp"
            type="tel"
            inputmode="tel"
            autocomplete="tel-national"
            :placeholder="t('benefit_form.whatsapp_placeholder')"
            :aria-invalid="Boolean(fieldErrors.whatsapp)"
            :aria-describedby="fieldErrors.whatsapp ? 'benefit-error-whatsapp' : undefined"
            required
          />
          <small
            v-if="fieldErrors.whatsapp"
            id="benefit-error-whatsapp"
            class="benefit-form__error"
          >
            {{ fieldErrors.whatsapp }}
          </small>
        </label>

        <label class="benefit-form__field benefit-form__field--wide">
          <span>{{ t("benefit_form.email") }}</span>
          <input
            id="benefit-email"
            v-model.trim="form.email"
            type="email"
            autocomplete="email"
            :placeholder="t('benefit_form.email_placeholder')"
            :aria-invalid="Boolean(fieldErrors.email)"
            :aria-describedby="fieldErrors.email ? 'benefit-error-email' : undefined"
            required
          />
          <small
            v-if="fieldErrors.email"
            id="benefit-error-email"
            class="benefit-form__error"
          >
            {{ fieldErrors.email }}
          </small>
        </label>
      </div>

      <p v-if="generalError" class="benefit-form__feedback" role="alert">
        {{ generalError }}
      </p>

      <div class="benefit-form__actions">
        <button
          v-if="!success"
          type="submit"
          class="benefit-form__submit"
          :disabled="isSubmitting"
        >
          <span v-if="!isSubmitting">{{ t("benefit_form.submit") }}</span>
          <span
            v-else
            class="benefit-form__spinner"
            role="status"
            aria-live="assertive"
          >
            {{ t("benefit_form.loading") }}
          </span>
        </button>

        <button
          v-else
          type="button"
          class="benefit-form__submit"
          @click="emit('close')"
        >
          {{ t("benefit_form.submit_done_cta") }}
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { createRedemption } from "@/api/benefits";
import { trackAllyEvent } from "@/api/metrics";
import { useAuth } from "@/composables/useAuth";
import Swal from "sweetalert2";

const props = defineProps({
  ally: {
    type: Object,
    required: true,
  },
  prefillUser: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["submitted", "close", "auth-required"]);

const { t } = useI18n();
const { requireLogin } = useAuth();

const form = reactive({
  doc_number: "",
  full_name: "",
  phone: "",
  whatsapp: "",
  email: "",
});

const fieldErrors = reactive({
  doc_number: "",
  full_name: "",
  phone: "",
  whatsapp: "",
  email: "",
});

const generalError = ref("");
const isSubmitting = ref(false);
const success = ref(false);

const emailPattern =
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const minLengthMap = {
  doc_number: 5,
  phone: 7,
  whatsapp: 7,
};

const applyPrefill = (profile) => {
  if (!profile) {
    return;
  }

  if (profile.doc_number && !form.doc_number) {
    form.doc_number = profile.doc_number;
  }
  if (profile.full_name && !form.full_name) {
    form.full_name = profile.full_name;
  } else if (!form.full_name) {
    const name = [profile.first_name, profile.last_name].filter(Boolean).join(" ");
    if (name) {
      form.full_name = name;
    }
  }
  if (profile.phone && !form.phone) {
    form.phone = profile.phone;
  }
  if (profile.whatsapp && !form.whatsapp) {
    form.whatsapp = profile.whatsapp;
  } else if (profile.phone && !form.whatsapp) {
    form.whatsapp = profile.phone;
  }
  if (profile.email && !form.email) {
    form.email = profile.email;
  }
};

watch(
  () => props.prefillUser,
  (profile) => {
    applyPrefill(profile);
  },
  { immediate: true, deep: true }
);

const resetErrors = () => {
  Object.keys(fieldErrors).forEach((key) => {
    fieldErrors[key] = "";
  });
  generalError.value = "";
};

const validate = () => {
  resetErrors();
  let valid = true;

  Object.entries(form).forEach(([key, value]) => {
    if (!value) {
      fieldErrors[key] = t("benefit_form.error_required");
      valid = false;
    }
  });

  Object.entries(minLengthMap).forEach(([key, min]) => {
    if (form[key] && String(form[key]).length < min) {
      fieldErrors[key] = t("benefit_form.error_min_length", { min });
      valid = false;
    }
  });

  if (form.email && !emailPattern.test(form.email)) {
    fieldErrors.email = t("benefit_form.error_email");
    valid = false;
  }

  return valid;
};

const parseBackendErrors = (errorResponse) => {
  const errors = errorResponse?.data?.errors || {};
  const message = errorResponse?.data?.message;

  if (typeof errors === "object" && errors !== null) {
    Object.entries(errors).forEach(([key, messages]) => {
      const firstMessage = Array.isArray(messages) ? messages[0] : messages;
      if (fieldErrors[key] !== undefined && !fieldErrors[key]) {
        fieldErrors[key] = firstMessage;
      }
    });
  }

  if (message) {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes("redim")) {
      generalError.value = t("benefit_form.error_already_redeemed");
      return;
    }
    if (lowerMessage.includes("validity") || lowerMessage.includes("vigencia") || lowerMessage.includes("vencid")) {
      generalError.value = t("benefit_form.error_out_of_force");
      return;
    }
    generalError.value = message;
  } else if (!generalError.value) {
    generalError.value = t("benefit_form.error_generic");
  }
};

const handleSubmit = async () => {
  if (!validate()) {
    return;
  }

  if (!props.ally?.id) {
    generalError.value = t("benefit_form.error_generic");
    return;
  }

  isSubmitting.value = true;
  generalError.value = "";

  try {
    const payload = {
      ...form,
      ally_id: props.ally.id,
    };

    const redemption = await createRedemption(props.ally.id, payload);
    const redemptionData = redemption?.data ?? redemption ?? null;
    const redemptionCode =
      redemptionData?.code ??
      null;
    const redemptionId =
      redemptionData?.id ??
      redemptionData?.uuid ??
      redemptionData?.identifier ??
      redemptionCode ??
      null;
    success.value = true;
    emit("submitted", redemption);

    if (redemptionId) {
      await trackAllyEvent(props.ally.id, "benefit_created", {
        ally_code: props.ally.code || props.ally.slug || null,
        ally_name: props.ally.name,
        redemption_id: redemptionId,
      });
    }

    const successAlertOptions = {
      icon: "success",
      title: t("benefit_form.success_alert_title"),
      confirmButtonText: t("benefit_form.success_alert_cta"),
    };

    if (redemptionCode) {
      successAlertOptions.html = `
        <p>${t("benefit_form.success_alert_body")}</p>
        <strong>${t("benefit_form.success_alert_code_label", { code: redemptionCode })}</strong>
      `.trim();
    } else {
      successAlertOptions.text = t("benefit_form.success_alert_body");
    }

    await Swal.fire(successAlertOptions);

  } catch (error) {
    const status = error?.response?.status;
    if (status === 401) {
      emit("auth-required");
      requireLogin();
      return;
    }

    if (status === 422) {
      parseBackendErrors(error?.response);
      return;
    }

    generalError.value = t("benefit_form.error_generic");
    console.error("Benefit form submission failed", error);
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<style scoped>
.benefit-form {
  background: #f9fafb;
  border-radius: 18px;
  padding: 24px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.benefit-form__header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.benefit-form__title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.benefit-form__subtitle {
  margin: 0;
  font-size: 0.95rem;
  color: #6b7280;
}

.benefit-form__body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.benefit-form__grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.benefit-form__field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 0.95rem;
  color: #111827;
}

.benefit-form__field input {
  border-radius: 12px;
  border: 1px solid rgba(107, 114, 128, 0.4);
  padding: 10px 14px;
  font-size: 0.95rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.benefit-form__field input:focus {
  outline: none;
  border-color: #f97316;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.15);
}

.benefit-form__field--wide {
  grid-column: 1 / -1;
}

.benefit-form__error {
  color: #dc2626;
  font-size: 0.8rem;
}

.benefit-form__feedback {
  color: #dc2626;
  font-weight: 500;
  margin: 0;
}

.benefit-form__actions {
  display: flex;
  justify-content: flex-end;
}

.benefit-form__submit {
  background: linear-gradient(135deg, #f97316, #ec4899);
  color: #ffffff;
  border: none;
  border-radius: 999px;
  font-size: 1rem;
  font-weight: 600;
  padding: 12px 28px;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.benefit-form__submit:disabled {
  opacity: 0.75;
  cursor: not-allowed;
}

.benefit-form__submit:not(:disabled):hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 20px rgba(236, 72, 153, 0.3);
}

.benefit-form__spinner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.benefit-form__state {
  border-radius: 16px;
  padding: 20px;
}

.benefit-form__state--success {
  background: rgba(16, 185, 129, 0.12);
  color: #065f46;
}

@media (max-width: 600px) {
  .benefit-form {
    padding: 20px;
  }

  .benefit-form__actions {
    justify-content: stretch;
  }

  .benefit-form__submit {
    width: 100%;
  }
}
</style>
